<template>
  <div class="rma-photo-uploader">
    <div class="upload-trigger">
      <KButton
        @click="openClickHandler($event)"
        :svg-icon="cameraIcon"
        :fill-mode="'link'"
        :disabled="disabled"
        :title="t('rma.UploadPhotos')"
      ></KButton>
      <span class="file-count">( {{ fileCount }} )</span>
    </div>
    
    <KDialog
      v-if="dialogVisible"
      :title="t('rma.UploadPhotosTitle')"
      @close="closeHandler"
      :width="600"
      :height="500"
    >
      <div class="upload-content">
        <div class="upload-section-title">
          {{ t('rma.UploadNewPhotos') }}
        </div>
        
        <KUpload
          :defaultFiles="defaultFiles"
          :batch="false"
          :multiple="true"
          :files="uploaderFiles"
          :auto-upload="false"
          :restrictions="{
            allowedExtensions: ['.jpeg', '.png', '.jpg', '.gif', '.mp4','.mov'],
            maxFileSize: 10000000,
          }"
          :with-credentials="false"
          @add="onAdd($event)"
          @remove="onRemove($event)"
        >
        </KUpload>
        
        <!-- Previously Uploaded Files Section -->
        <div v-if="existingUploadFileInfos.length > 0" class="existing-files-section">
          <div class="upload-section-title">
            {{ t('rma.PreviouslyUploadedPhotos') }}
          </div>
          <div class="existing-files-list">
            <div
              v-for="existingFile in existingUploadFileInfos"
              :key="existingFile.uid"
              class="existing-file-item"
            >
              <span class="file-name">{{ existingFile.name }}</span>
              <KButton
                @click="deleteExistingFile(existingFile)"
                :svg-icon="trashIcon"
                :fill-mode="'flat'"
                size="small"
                :title="t('buttons.Delete')"
              ></KButton>
            </div>
          </div>
        </div>
      </div>
      
      <KDialogActionsBar>
        <KButton
          @click="closeHandler"
          :fill-mode="'outline'"
        >
          {{ t("buttons.Exit") }}
        </KButton>
        <KButton
          @click="saveHandler"
          :fill-mode="'solid'"
          :theme-color="'primary'"
        >
          {{ t("buttons.Save") }}
        </KButton>
      </KDialogActionsBar>
    </KDialog>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { ref, computed, watch, nextTick } from 'vue'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import {
  Upload as KUpload,
  type UploadOnAddEvent,
  type UploadOnRemoveEvent,
  type UploadFileInfo,
  UploadFileStatus,
} from '@progress/kendo-vue-upload'
import { Dialog as KDialog, DialogActionsBar as KDialogActionsBar } from '@progress/kendo-vue-dialogs'
import { cameraIcon, trashIcon } from '@progress/kendo-svg-icons'
import { useLogger } from 'vue-logger-plugin'

const logger = useLogger()

// Interface for existing uploaded files from the API
interface ExistingFileData {
  name?: string
  fileName?: string
  id?: number
  uid?: string
}

const componentProps = defineProps({
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  files: {
    type: Array<UploadFileInfo>,
    required: true,
  },
  existingFiles: {
    type: Array<ExistingFileData>,
    required: false,
    default: () => [],
  },
  deletedFiles: {
    type: Array<UploadFileInfo>,
    required: true,
  },
})

const emit = defineEmits(['update:files', 'update:deletedFiles', 'save'])

const defaultFiles = ref<UploadFileInfo[]>([])
const uploaderFiles = ref(componentProps.files)
const deletedFiles = ref(componentProps.deletedFiles)
const dialogVisible = ref(false)

const existingUploadFileInfos = computed(() => {
  if (componentProps.existingFiles && componentProps.existingFiles.length > 0) {
    return componentProps.existingFiles
      .map((file: ExistingFileData) => {
        return {
          name: file.name || file.fileName || 'Unknown',
          uid: file.id?.toString() || file.uid,
          status: UploadFileStatus.Uploaded,
          progress: 100,
        } as UploadFileInfo
      })
      .filter(
        (file) =>
          !deletedFiles.value.some(
            (deletedFile) => deletedFile.uid === file.uid
          )
      )
  }
  return []
})

const fileCount = computed(
  () => uploaderFiles.value.length + existingUploadFileInfos.value.length
)

function onAdd(e: UploadOnAddEvent) {
  logger.debug('onAdd', e)
  uploaderFiles.value = e.affectedFiles
  emit('update:files', uploaderFiles.value)
}

function onRemove(e: UploadOnRemoveEvent) {
  logger.debug('onRemove', e)
  if (uploaderFiles.value.length > 0) {
    uploaderFiles.value = uploaderFiles.value.filter(
      (file) => file.uid !== e.affectedFiles[0].uid
    )
  }
  if (defaultFiles.value.length > 0) {
    const file = defaultFiles.value.find(
      (file) => file.uid === e.affectedFiles[0].uid
    )
    if (file) {
      deletedFiles.value.push(file)
      emit('update:deletedFiles', deletedFiles.value)
      defaultFiles.value = defaultFiles.value.filter(
        (file) => file.uid !== e.affectedFiles[0].uid
      )
    }
  }
  emit('update:files', uploaderFiles.value)
}

async function openClickHandler(event: MouseEvent) {
  logger.debug('openClickHandler', event)
  dialogVisible.value = true
  defaultFiles.value = componentProps.files
  
  // Wait for dialog to render, then scroll it into view
  await nextTick()
  setTimeout(() => {
    const dialogElement = document.querySelector('.k-dialog-wrapper')
    if (dialogElement) {
      dialogElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, 100)
}

function closeHandler(...args: unknown[]) {
  logger.debug('closeHandler', args)
  dialogVisible.value = false
}

function deleteExistingFile(file: UploadFileInfo) {
  logger.debug('deleteExistingFile', file)
  deletedFiles.value.push(file)
  emit('update:deletedFiles', deletedFiles.value)
}

function saveHandler() {
  logger.debug('saveHandler')
  dialogVisible.value = false
  emit('save')
}

watch(
  () => componentProps.files,
  (newFiles) => {
    if (newFiles) {
      uploaderFiles.value = newFiles
    } else {
      uploaderFiles.value = []
    }
  },
  { immediate: false, deep: true }
)

watch(
  () => componentProps.deletedFiles,
  (newDeletedFiles) => {
    if (newDeletedFiles) {
      deletedFiles.value = newDeletedFiles
    } else {
      deletedFiles.value = []
    }
  },
  { immediate: false, deep: true }
)
</script>

<style lang="scss" scoped>
.rma-photo-uploader {
  display: inline-block;
}

.upload-trigger {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.file-count {
  @include typography-small;
  color: #6c757d;
}

.upload-content {
  padding: 1rem;
}

.upload-section-title {
  @include font-weight-bold;
  margin-bottom: 0.75rem;
  margin-top: 1rem;
}

.upload-section-title:first-child {
  margin-top: 0;
}

.existing-files-section {
  margin-top: 1.5rem;
}

.existing-files-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.existing-file-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background-color: var(--vt-c-white-soft);
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  transition: background-color 0.2s;
}

.existing-file-item:hover {
  background-color: #e9ecef;
}

.file-name {
  @include typography-body;
  color: #495057;
}
</style>

<style lang="scss">
.rma-photo-uploader .k-dialog .k-upload .k-actions {
  display: none;
}

.rma-photo-uploader .k-dialog .k-dialog-content {
  overflow-y: auto;
  max-height: calc(500px - 120px);
}
</style>
